/**
 * Solana settlement networks for the flat JoinSplit V2 stack.
 *
 * A network is enabled by configuring its pool program id; everything else
 * has a safe default. A malformed value disables the network with a reason
 * instead of throwing: the EVM app keeps working when a Solana variable is
 * wrong, and the Solana route fails closed rather than talk to a pool it
 * cannot trust.
 */
import {
  SOLANA_TOKEN_PROGRAM_ID,
  solanaChainDomainId,
  solanaPubkeyBase58,
  type SolanaCluster,
} from "@stealthos/sdk";

type SolanaNetworkKey = "solana-devnet";

export interface SolanaTokenInfo {
  /** Mint address, or `null` for native SOL. */
  mint: string | null;
  tokenProgram: string;
  symbol: string;
  name: string;
  decimals: number;
  isNative: boolean;
  /** Display identity is independent of a market quote. */
  iconId?: string;
  testToken?: boolean;
}

export interface SolanaNetworkConfig {
  key: SolanaNetworkKey;
  cluster: SolanaCluster;
  label: string;
  /** Path the app is mounted at. */
  route: string;
  /** False until the pool program id is configured and every value parses. */
  enabled: boolean;
  /** Set when a variable is present but unusable; the network stays disabled. */
  configurationError?: string;
  programId: string;
  chainDomainId: bigint;
  /** Transactions before this slot are never pool transactions. */
  deploymentSlot: number;
  /** Same-origin JSON-RPC proxy; the upstream URL is server configuration. */
  rpcUrl: string;
  engineProxyUrl: string;
  relayProxyUrl: string;
  relayPath: string;
  explorerUrl: string;
  explorerClusterQuery: string;
  /** Serve a pool built with development verifying keys (never mainnet-beta). */
  allowDevelopmentSetup: boolean;
  /**
   * Explicit deployment capability. Keep false until this program id exposes
   * the reviewed DepositMergeV1 instructions and matching verifier key.
   */
  depositMergeEnabled?: boolean;
  /** Enable only after the durable-record program upgrade and legacy leaf hydration. */
  onchainRecoveryEnabled?: boolean;
  /** User-configurable public RPC for independent recovery; never a StealthOS proxy. */
  recoveryRpcUrl?: string;
  tokens: SolanaTokenInfo[];
  /** Finality policy (D8): leaf assignment and journal completion wait for it. */
  commitment: "finalized";
  /** Caps full tree rebuilds caused by untrusted tree claims in one sync. */
  maxTreeScans: number;
}

const SOLANA_RPC_PROXY_PATH = "/api/proxy/solana-rpc";
const SOLANA_RELAY_PATH = "/api/v1/relay/solana/pool";
const ENGINE_PROXY_URL = "/api/proxy/solana-data";
const DEFAULT_EXPLORER_URL = "https://explorer.solana.com";
/** Circle's devnet USDC. */
const DEVNET_USDC_MINT = "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU";
/** Zenrock's real wrapped ZEC mint on mainnet, never a devnet default. */
export const MAINNET_ZENZEC_MINT =
  "JDt9rRGaieF6aN1cJkXFeUmsy7ZE4yY3CZb8tVMXVroS";
export const SOLANA_PRICE_ASSETS: Readonly<Record<string, string>> = {
  sol: "solana",
  [DEVNET_USDC_MINT]: "usd-coin",
  [MAINNET_ZENZEC_MINT]: "zcash",
};

export function solanaPriceAssets(
  tokens: readonly SolanaTokenInfo[],
): Record<string, string> {
  return Object.fromEntries(
    tokens.flatMap((token) => {
      const key = token.mint ?? "sol";
      const market = SOLANA_PRICE_ASSETS[key];
      return market && !token.testToken ? [[key, market]] : [];
    }),
  );
}

/** Never trust UI decimals without checking the registered pool asset. */
export function assertSolanaRegisteredToken(
  network: SolanaNetworkConfig,
  mint: string | null,
  actual: {
    mint: string;
    tokenProgram: string;
    decimals: number;
    isNative: boolean;
  },
): void {
  const configured = network.tokens.find((token) => token.mint === mint);
  if (!configured) throw new Error("Asset is not configured in this wallet");
  const expectedMint = mint ?? "11111111111111111111111111111111";
  if (
    actual.mint !== expectedMint ||
    actual.decimals !== configured.decimals ||
    actual.isNative !== configured.isNative ||
    (mint !== null && actual.tokenProgram !== configured.tokenProgram)
  )
    throw new Error(
      "Registered pool token identity, program or decimals differs from wallet configuration",
    );
}

export function solanaTokenLabel(token: SolanaTokenInfo): string {
  return token.testToken ? `${token.symbol} (devnet)` : token.symbol;
}

type Env = Record<string, string | undefined>;

function text(env: Env, name: string): string {
  return String(env[name] ?? "").trim();
}

function integer(
  env: Env,
  name: string,
  fallback: number,
  errors: string[],
  { min }: { min: number },
): number {
  const raw = text(env, name);
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed < min) {
    errors.push(`${name} must be an integer >= ${min}`);
    return fallback;
  }
  return parsed;
}

function flag(env: Env, name: string, fallback: boolean, errors: string[]) {
  const raw = text(env, name).toLowerCase();
  if (!raw) return fallback;
  if (raw === "true") return true;
  if (raw === "false") return false;
  errors.push(`${name} must be true or false`);
  return fallback;
}

/** Parse a decimal token amount ("0.005") into base units. */
export function parseSolanaTokenAmount(
  value: string,
  decimals: number,
): bigint {
  const trimmed = String(value ?? "").trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    throw new Error(`"${value}" is not a decimal amount`);
  }
  const [whole, fraction = ""] = trimmed.split(".");
  if (fraction.length > decimals) {
    throw new Error(`"${value}" has more than ${decimals} decimal places`);
  }
  return BigInt(whole + fraction.padEnd(decimals, "0"));
}

function pubkey(
  env: Env,
  name: string,
  fallback: string,
  errors: string[],
): string {
  const raw = text(env, name) || fallback;
  if (!raw) return "";
  try {
    return solanaPubkeyBase58(raw, name);
  } catch {
    errors.push(`${name} is not a base58 32-byte address`);
    return "";
  }
}

/**
 * Read one network's configuration from `REACT_APP_SOLANA_<NET>_*`. Pure, so
 * tests can exercise every branch without rebuilding the module.
 */
export function readSolanaNetworkConfig(
  env: Env,
  key: SolanaNetworkKey,
): SolanaNetworkConfig {
  const prefix = "REACT_APP_SOLANA_DEVNET_";
  const cluster: SolanaCluster = "devnet";
  const errors: string[] = [];
  const programId = pubkey(env, `${prefix}POOL_PROGRAM_ID`, "", errors);
  const usdcMint = pubkey(env, `${prefix}USDC_MINT`, DEVNET_USDC_MINT, errors);
  const zenZecMint = pubkey(env, `${prefix}ZENZEC_MINT`, "", errors);
  if (
    zenZecMint &&
    (zenZecMint === usdcMint || zenZecMint === MAINNET_ZENZEC_MINT)
  ) {
    errors.push(`${prefix}ZENZEC_MINT must be a separate devnet stand-in mint`);
  }
  const explorerUrl =
    text(env, `${prefix}EXPLORER_URL`).replace(/\/+$/, "") ||
    DEFAULT_EXPLORER_URL;
  if (!/^https:\/\//.test(explorerUrl)) {
    errors.push(`${prefix}EXPLORER_URL must be an https URL`);
  }
  const tokens: SolanaTokenInfo[] = [
    {
      mint: null,
      tokenProgram: SOLANA_TOKEN_PROGRAM_ID,
      symbol: "SOL",
      name: "Solana",
      decimals: 9,
      isNative: true,
    },
  ];
  if (usdcMint) {
    tokens.push({
      mint: usdcMint,
      tokenProgram: SOLANA_TOKEN_PROGRAM_ID,
      symbol: "USDC",
      name: "USD Coin (devnet)",
      decimals: 6,
      isNative: false,
    });
  }
  if (
    zenZecMint &&
    zenZecMint !== usdcMint &&
    zenZecMint !== MAINNET_ZENZEC_MINT
  ) {
    tokens.push({
      mint: zenZecMint,
      tokenProgram: SOLANA_TOKEN_PROGRAM_ID,
      symbol: "zenZEC",
      name: "zenZEC (devnet)",
      decimals: 8,
      isNative: false,
      iconId: "zcash",
      testToken: true,
    });
  }
  const config: SolanaNetworkConfig = {
    key,
    cluster,
    label: "Solana Devnet",
    route: "/solana",
    enabled: false,
    programId,
    chainDomainId: solanaChainDomainId(cluster),
    deploymentSlot: integer(env, `${prefix}DEPLOYMENT_SLOT`, 0, errors, {
      min: 0,
    }),
    rpcUrl: SOLANA_RPC_PROXY_PATH,
    engineProxyUrl: `${ENGINE_PROXY_URL}?network=${cluster}&program=${programId}&path=`,
    relayProxyUrl: "/api/proxy/base-engine?path=",
    relayPath: SOLANA_RELAY_PATH,
    explorerUrl,
    explorerClusterQuery: `?cluster=${cluster}`,
    allowDevelopmentSetup: flag(
      env,
      `${prefix}ALLOW_DEVELOPMENT_SETUP`,
      false,
      errors,
    ),
    depositMergeEnabled: flag(
      env,
      `${prefix}DEPOSIT_MERGE_ENABLED`,
      false,
      errors,
    ),
    onchainRecoveryEnabled: flag(
      env,
      `${prefix}ONCHAIN_RECOVERY_ENABLED`,
      false,
      errors,
    ),
    recoveryRpcUrl:
      text(env, `${prefix}RECOVERY_RPC_URL`) || "https://api.devnet.solana.com",
    tokens,
    commitment: "finalized",
    maxTreeScans: 8,
  };
  if (errors.length > 0) {
    return { ...config, enabled: false, configurationError: errors.join("; ") };
  }
  return { ...config, enabled: Boolean(programId) };
}

const SOLANA_DEVNET: SolanaNetworkConfig = readSolanaNetworkConfig(
  process.env,
  "solana-devnet",
);

export const SOLANA_NETWORKS: readonly SolanaNetworkConfig[] = [SOLANA_DEVNET];

export function getSolanaNetwork(key: string): SolanaNetworkConfig | undefined {
  return SOLANA_NETWORKS.find((network) => network.key === key);
}

/** The network, or a clear error when it is missing or disabled. */
export function requireSolanaNetwork(key: string): SolanaNetworkConfig {
  const network = getSolanaNetwork(key);
  if (!network) throw new Error(`Unknown Solana network "${key}"`);
  if (!network.enabled) {
    throw new Error(
      network.configurationError
        ? `${network.label} is misconfigured: ${network.configurationError}`
        : `${network.label} is not configured in this deployment (set REACT_APP_SOLANA_DEVNET_POOL_PROGRAM_ID)`,
    );
  }
  return network;
}

export function findSolanaToken(
  network: SolanaNetworkConfig,
  mint: string | null,
): SolanaTokenInfo | undefined {
  return network.tokens.find((token) => token.mint === mint);
}

export function solanaExplorerTxUrl(
  network: SolanaNetworkConfig,
  signature: string,
): string {
  return `${network.explorerUrl}/tx/${encodeURIComponent(signature)}${network.explorerClusterQuery}`;
}

export function solanaExplorerAddressUrl(
  network: SolanaNetworkConfig,
  address: string,
): string {
  return `${network.explorerUrl}/address/${encodeURIComponent(address)}${network.explorerClusterQuery}`;
}

/** Format base units as a decimal string without float rounding. */
export function formatSolanaTokenAmount(
  value: bigint,
  decimals: number,
): string {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const base = 10n ** BigInt(decimals);
  const whole = magnitude / base;
  const fraction = (magnitude % base)
    .toString()
    .padStart(decimals, "0")
    .replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}
